import { useState } from "react";
import { Download, MessageSquare } from "lucide-react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { MANAGEMENT_TOPICS } from "features/cdm/constants/carePlanOptions";
import { useDownloadCarePlanPdf } from "features/cdm/hooks/useDownloadCarePlanPdf";
import { useGetCarePlan } from "features/cdm/hooks/useGetCarePlan";
import { useSendCarePlanSms } from "features/cdm/hooks/useSendCarePlanSms";
import { formatCarePlanDate } from "features/cdm/utils/carePlanDate";
import { ensureAbsoluteUrl } from "features/cdm/utils/ensureAbsoluteUrl";
import { usePatientData } from "features/patients/hooks/usePatientData";

import style from "./CarePlanViewModal.module.css";

const topicLabel = (topic) =>
  MANAGEMENT_TOPICS.find((item) => item.topic === topic)?.label ?? topic;

const TextRow = ({ label, value }) =>
  value ? (
    <div className={style.row}>
      <p className={style.label}>{label}</p>
      <p className={style.value}>{value}</p>
    </div>
  ) : null;

const ListSection = ({ title, items, renderItem }) =>
  items?.length ? (
    <div className={style.section}>
      <h4>{title}</h4>
      <ul className={style.list}>
        {items.map((item, index) => (
          <li key={index}>{renderItem(item)}</li>
        ))}
      </ul>
    </div>
  ) : null;

export const CarePlanViewModal = ({ isOpen, onClose, patientId, reviewId }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const { data: carePlanView, isLoading } = useGetCarePlan(
    isOpen ? reviewId : null
  );
  const { data: patient } = usePatientData(patientId);
  const { mutate: downloadPdf, isPending: isDownloading } =
    useDownloadCarePlanPdf();
  const { mutate: sendSms, isPending: isSendingSms } = useSendCarePlanSms();

  const [isSmsConfirmOpen, setIsSmsConfirmOpen] = useState(false);
  const [mobileOverride, setMobileOverride] = useState("");

  if (!isOpen) return null;

  const carePlan = carePlanView?.carePlan;
  const hasReview = !!carePlanView?.latestReviewId;
  const hasInfoLinks = !!carePlanView?.infoLinks?.length;
  const registeredMobile = patient?.mobilePhone || "";

  const handleSendSms = () => {
    sendSms(
      { reviewId, mobileOverride: mobileOverride.trim() },
      {
        onSuccess: () => {
          setIsSmsConfirmOpen(false);
          setMobileOverride("");
        },
      }
    );
  };

  return (
    <Dialog open title="Personalised Care Plan" onClose={onClose} width={720}>
      {isLoading && <p className={style.muted}>Loading care plan…</p>}

      {!isLoading && !hasReview && (
        <p className={style.muted}>
          No care plan yet — submit a CDM or PP review to create one.
        </p>
      )}

      {!isLoading && hasReview && (
        <div className={style.content}>
          {!!carePlan?.carerName && (
            <div className={style.section}>
              <h4>Carer / Family member</h4>
              <TextRow label="Name" value={carePlan.carerName} />
              <TextRow
                label="Consent to disclose"
                value={carePlan.carerConsentToDisclose ? "Yes" : "No"}
              />
            </div>
          )}

          <ListSection
            // KIN-1195: only a true snapshot flag means the list is the
            // review's point-in-time record ("Medications"). false OR absent
            // (today, pre-KIN-1195) means it's the patient's live list.
            title={
              carePlanView.medicationsFromReviewSnapshot === true
                ? "Medications"
                : "Current medications (as of today)"
            }
            items={carePlanView.medications}
            renderItem={(medication) =>
              [
                medication.name,
                medication.strength,
                medication.form,
                medication.dosage,
                medication.frequency,
              ]
                .filter(Boolean)
                .join(" · ")
            }
          />

          <ListSection
            title="Vaccinations"
            items={carePlanView.administeredVaccines}
            renderItem={(vaccine) =>
              [vaccine.vaccineName, vaccine.type, vaccine.dateGiven]
                .filter(Boolean)
                .join(" · ")
            }
          />

          {!!carePlan && (
            <div className={style.section}>
              <h4>Medications &amp; Wellbeing</h4>
              <TextRow
                label="Medication concerns"
                value={carePlan.medicationConcerns}
              />
              <TextRow
                label="Issues discussed about medications"
                value={carePlan.medicationIssuesDiscussed}
              />
              <TextRow label="Health concerns" value={carePlan.healthConcerns} />
              <TextRow
                label="Health improvements"
                value={carePlan.healthImprovements}
              />
              <TextRow
                label="Other things that matter"
                value={carePlan.thingsThatMatter}
              />
              <TextRow
                label="Health issues discussed today"
                value={carePlan.healthIssuesDiscussed}
              />
            </div>
          )}

          <ListSection
            title="What I do to manage my condition"
            items={carePlan?.conditionManagement}
            renderItem={(item) =>
              `${topicLabel(item.topic)}${item.detail ? `: ${item.detail}` : ""}`
            }
          />

          <ListSection
            title="Goals from last review"
            items={carePlan?.previousGoals}
            renderItem={(goal) =>
              `${goal.description}${goal.progress ? ` — ${goal.progress}` : ""}`
            }
          />

          <ListSection
            title="Goals agreed at this review"
            items={carePlan?.currentGoals}
            renderItem={(goal) =>
              `${goal.description}${goal.plan ? ` — ${goal.plan}` : ""}`
            }
          />

          {!!carePlan?.anticipatoryCarePlan && (
            <div className={style.section}>
              <h4>Anticipatory care plan</h4>
              <p className={style.value}>{carePlan.anticipatoryCarePlan}</p>
            </div>
          )}

          <ListSection
            title="Referrals (last 6 months)"
            items={carePlan?.referrals}
            renderItem={(referral) =>
              `${referral.service}${referral.detail ? `: ${referral.detail}` : ""}`
            }
          />

          <ListSection
            title="Take-home messages"
            items={carePlan?.takeHomeMessages}
            renderItem={(message) => message}
          />

          <TextRow
            label="Next review date"
            value={formatCarePlanDate(carePlan?.nextReviewDate)}
          />

          <ListSection
            title="Sources of information"
            items={carePlanView.infoLinks}
            renderItem={(link) => (
              <>
                <span className={style.label}>{link.label}</span>:{" "}
                <a
                  href={ensureAbsoluteUrl(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.url}
                </a>
              </>
            )}
          />
        </div>
      )}

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => setIsSmsConfirmOpen(true)}
          disabled={!hasReview || !hasInfoLinks || isSendingSms}
        >
          <MessageSquare size={16} />
          Send links by SMS
        </Button>
        <Button
          onClick={() => downloadPdf({ reviewId, tenantVal })}
          disabled={isDownloading || !hasReview}
        >
          <Download size={16} />
          {isDownloading ? "Downloading…" : "Download / Print handout"}
        </Button>
      </DialogFooter>

      {isSmsConfirmOpen && (
        <Dialog
          open
          title="Send care plan links by SMS"
          onClose={() => setIsSmsConfirmOpen(false)}
          width={440}
          zIndex={1400}
        >
          <div className={style.smsConfirm}>
            <p className={style.value}>
              The patient will be charged for this SMS. The health-info links
              will be sent to the number below.
            </p>
            <div className={style.row}>
              <p className={style.label}>Registered mobile</p>
              <p className={style.value}>{registeredMobile || "—"}</p>
            </div>
            <div className={style.row}>
              <label className={style.label} htmlFor="care-plan-sms-override">
                Send to a different number (optional)
              </label>
              <input
                id="care-plan-sms-override"
                className={style.smsInput}
                type="tel"
                value={mobileOverride}
                onChange={(event) => setMobileOverride(event.target.value)}
                placeholder="e.g. 0851234567"
              />
            </div>
          </div>
          <div className={style.smsFooter}>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsSmsConfirmOpen(false)}
                disabled={isSendingSms}
              >
                Cancel
              </Button>
              <Button onClick={handleSendSms} disabled={isSendingSms}>
                {isSendingSms ? "Sending…" : "Send SMS"}
              </Button>
            </DialogFooter>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
};
